import type { MessageDescriptor } from '@lingui/core';
import { msg } from '@lingui/core/macro';
import { Trans, Plural, useLingui } from '@lingui/react/macro';
import { useState, useRef, useCallback, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { LogoutOutlined, SettingOutlined } from '@ant-design/icons';
import { useAuthStore, useAgentStore } from '../stores';
import { logout } from '../lib/api';
import { useDismissibleLayer } from '../hooks/useDismiss';
import { runUnsavedGuards } from '../hooks/useUnsavedChangesGuard';
import { routes } from '../lib/routes';
import { UserBadge } from './UserBadge';
import { UserAvatar } from './UserAvatar';

interface Props {
  compact?: boolean;
  onOpenSettings?: (restoreFocus: () => void) => void;
  onLogout?: () => void;
  onLogoutStart?: () => void | (() => void);
  onLogoutFailure?: () => void;
}

export default function UserPanel({
  compact = false,
  onOpenSettings,
  onLogout,
  onLogoutStart,
  onLogoutFailure,
}: Props) {
  const { t, i18n } = useLingui();
  const currentUser = useAuthStore((s) => s.currentUser);
  const agentCount = useAgentStore((s) => s.agents.length);
  const agentsUnknown = useAgentStore((s) => s.loadFailed && s.agents.length === 0);
  const loadAgents = useAgentStore((s) => s.loadAgents);
  const navigate = useNavigate();
  const location = useLocation();
  const isAgents = location.pathname.startsWith('/agents');
  const [showAccountPanel, setShowAccountPanel] = useState(false);
  const [logoutError, setLogoutError] = useState<MessageDescriptor | null>(null);
  const accountPanelRef = useRef<HTMLDivElement>(null);
  const accountTriggerRef = useRef<HTMLButtonElement>(null);

  const closeAccountPanel = useCallback(() => setShowAccountPanel(false), []);
  useDismissibleLayer({
    active: showAccountPanel,
    within: accountPanelRef,
    onEscape: closeAccountPanel,
    onOutsidePress: closeAccountPanel,
  });

  useEffect(() => {
    if (!compact) loadAgents();
  }, [compact, loadAgents]);

  const handleLogout = async () => {
    setLogoutError(null);
    const completeLogout = onLogoutStart?.();
    try {
      await logout();
      completeLogout?.();
      onLogout?.();
    } catch {
      onLogoutFailure?.();
      setLogoutError(msg`Could not sign out. Try again.`);
    }
  };

  if (!currentUser) return null;

  return (
    <div
      className={`user-panel${compact ? ' user-panel-compact' : ''}`}
      data-testid="sidebar-footer-primary"
    >
      <div className="user-panel-inner" data-testid="sidebar-footer-primary-actions">
        <div className="sidebar-account" ref={accountPanelRef}>
          {compact ? (
            <button
              type="button"
              ref={accountTriggerRef}
              className="sidebar-account-trigger"
              aria-label={t`Profile and settings`}
              title={t`Profile and settings`}
              aria-expanded={showAccountPanel}
              aria-controls={showAccountPanel ? 'sidebar-account-panel' : undefined}
              onClick={() => setShowAccountPanel((open) => !open)}
              data-testid="sidebar-account-trigger"
            >
              <UserAvatar
                userId={currentUser.id}
                displayName={currentUser.display_name}
                avatarUrl={currentUser.avatar_url}
                size={32}
                role={currentUser.role}
              />
            </button>
          ) : (
            <UserBadge
              userId={currentUser.id}
              displayName={currentUser.display_name}
              role={currentUser.role}
              avatarUrl={currentUser.avatar_url}
              size={28}
              onAvatarClick={() => setShowAccountPanel((open) => !open)}
              data-testid="sidebar-account-trigger"
            />
          )}
          {showAccountPanel && (
            <div
              id="sidebar-account-panel"
              className="sidebar-account-panel"
              data-testid="sidebar-account-panel"
              role="dialog"
              aria-label={t`Account`}
            >
              <div className="sidebar-account-panel-title">
                <Trans>Account</Trans>
              </div>
              <div className="sidebar-account-summary">
                <UserAvatar
                  userId={currentUser.id}
                  displayName={currentUser.display_name}
                  size={32}
                />
                <div className="sidebar-account-summary-text">
                  <div className="sidebar-account-name" data-testid="sidebar-account-name">
                    {currentUser.display_name}
                  </div>
                  <div className="sidebar-account-role" data-testid="sidebar-account-role">
                    {currentUser.role === 'agent' ? t`Agent` : t`Human`}
                  </div>
                </div>
              </div>
              <div className="sidebar-account-divider" />
              {onOpenSettings && <button
                type="button"
                className="sidebar-footer-secondary-item"
                data-testid="sidebar-account-settings"
                onClick={() => {
                  setShowAccountPanel(false);
                  onOpenSettings(() => accountTriggerRef.current?.focus());
                }}
              >
                <SettingOutlined aria-hidden="true" />
                <span><Trans>Settings</Trans></span>
              </button>}
              {onLogout && (
                <button
                  type="button"
                  className="sidebar-footer-secondary-item logout-btn"
                  title={t`Logout`}
                  data-testid="sidebar-account-logout"
                  onClick={() => {
                    setShowAccountPanel(false);
                    void handleLogout();
                  }}
                >
                  <LogoutOutlined aria-hidden="true" />
                  <span>
                    <Trans>Logout</Trans>
                  </span>
                </button>
              )}
            </div>
          )}
        </div>
        {!compact && currentUser.role !== 'agent' && (
          <span
            className={`user-panel-agents-link${isAgents ? ' user-panel-agents-link-active' : ''}`}
            role="button"
            tabIndex={0}
            onClick={() => {
              if (!runUnsavedGuards(undefined, i18n)) return;
              navigate(routes.agents());
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                if (!runUnsavedGuards(undefined, i18n)) return;
                navigate(routes.agents());
              }
            }}
          >
            {agentCount > 0 ? (
              <Trans>
                with <strong>{agentCount}</strong>{' '}
                <Plural value={agentCount} one="agent" other="agents" />
              </Trans>
            ) : agentsUnknown ? (
              // A list we could not load is not a list of none.
              <>
                <Trans>your agents</Trans>
              </>
            ) : (
              <>
                <Trans>+ create agent</Trans>
              </>
            )}
          </span>
        )}
      </div>
      {logoutError && (
        <div className="login-error" role="alert">
          {i18n._(logoutError)}
        </div>
      )}
    </div>
  );
}
